import { useMemo } from 'react';
import { OptimizedImage } from '@components/optimized-image';
import { extractDateTimeParts } from '@utils/date-time-format/extract-date-time-parts';
import classNames from 'classnames';

import { ImageDateProps } from './image-date.type';

export const ImageDate = ({
  image,
  name,
  date,
  width,
  height,
  round,
  timezone,
  locale,
  dayFont = 'text-headline-m text-white',
  monthFont = 'text-subtitle-s text-white',
  showYear = false,
}: ImageDateProps) => {
  const imageRadius = {
    left: 'rounded-tl-lg rounded-bl-lg',
    right: 'rounded-tr-lg rounded-br-lg',
    top: 'rounded-tr-lg rounded-tl-lg',
    bottom: 'rounded-br-lg rounded-bl-lg',
    full: 'rounded-lg',
  };

  // Extract all date parts with a single call
  const dateParts = useMemo(() => {
    if (!date) return null;
    return extractDateTimeParts(date, locale, timezone, {
      dateStyle: 'veryShort',
      day: 'numeric',
      year: showYear ? '2-digit' : 'numeric',
    });
  }, [date, locale, timezone, showYear]);

  return (
    <>
      <OptimizedImage
        className={classNames('object-cover w-full h-full', round ? imageRadius[round] : '')}
        src={image}
        alt={name}
        width={width}
        height={height}
        dataTestid='image-date'
      />
      <div
        className={classNames(
          'absolute top-0 left-0 w-full h-full shadow-l custom-gradient-overlay z-1',
          round ? imageRadius[round] : ''
        )}>
        <div className='flex items-center uppercase justify-center h-full w-full flex-col'>
          {!!date && !!dateParts && (
            <>
              <p className={classNames('text-white font-black', dayFont)}>
                <span data-testid='image-date__day-letter'>{dateParts.weekday}</span>
                <span data-testid='image-date__day-number'>{dateParts.day}</span>
              </p>
              <p
                className={classNames('flex whitespace-nowrap text-white font-black', monthFont)}
                data-testid='image-date__month'>
                {dateParts.month} {showYear && dateParts.year}
              </p>
            </>
          )}
        </div>
      </div>
    </>
  );
};
